/**
 * ProtectedRoute - Guards routes behind authentication.
 * WHY: Redirects unauthenticated users to login.
 * Handles logout by using prompt=login to force Keycloak re-authentication.
 *
 * Logout flow:
 * 1. user-header.tsx: Set flag → clear storage → redirect to Cognito logout
 * 2. Cognito clears its session → redirects back to /
 * 3. Here: Detect flag → call signinRedirect({ prompt: 'login' })
 * 4. Cognito forwards prompt=login to Keycloak → Keycloak shows login page
 *
 * WHY prompt=login instead of Keycloak logout endpoint:
 * The Keycloak logout endpoint requires id_token_hint to work reliably.
 * Since data-validation uses Cognito (not keycloak-js), we don't have the
 * Keycloak id_token. The prompt=login approach forces Keycloak to show the
 * login page regardless of existing session state.
 * See: https://docs.aws.amazon.com/cognito/latest/developerguide/authorization-endpoint.html
 */
import { useRef } from 'react';
import { useAuth } from 'react-oidc-context';
import { LoadingInitializing } from '@/components/loadings/LoadingInitializing';
import { SESSION_KEYS } from '@/lib/constants';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({ children }) => {
  const auth = useAuth();
  const redirectingRef = useRef(false);

  if (auth.isLoading) {
    return (
      <LoadingInitializing
        message="Initializing Application"
        subMessage="Please wait while we set up your session..."
      />
    );
  }

  if (auth.error) {
    return (
      <LoadingInitializing
        message="Authentication Error"
        subMessage="Redirecting to login..."
      />
    );
  }

  if (!auth.isAuthenticated) {
    // WHY: Prevent multiple signinRedirect calls from concurrent renders.
    if (!redirectingRef.current) {
      redirectingRef.current = true;

      const logoutFlag = sessionStorage.getItem(SESSION_KEYS.LOGOUT_IN_PROGRESS);
      if (logoutFlag) {
        sessionStorage.removeItem(SESSION_KEYS.LOGOUT_IN_PROGRESS);
        // WHY: prompt=login forces Cognito to forward this to Keycloak,
        // making Keycloak show the login page even if it has an active session.
        auth.signinRedirect({ prompt: 'login' });
      } else {
        auth.signinRedirect();
      }
    }
    return null;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
